import { computed, createApp } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { getContextualHelpConfig } from 'dashboard/constants/contextualHelp';
import { useStore } from 'dashboard/composables/store';
import ProductTourModal from 'dashboard/components-next/ProductTour/ProductTourModal.vue';

const WAIT_INTERVAL = 100;
const WAIT_TIMEOUT = 3000;
const OPTIONAL_WAIT_TIMEOUT = 8000;
const LICENSE_VALIDATION_WAIT_TIMEOUT = 30000;
const TOUR_BLOCKING_SELECTORS = ['#chatplan-license-paywall'];
export const PRODUCT_TOUR_TYPES = {
  WELCOME: 'welcome',
  CONTEXTUAL: 'contextual',
};

const WELCOME_TOUR_BLOCK_KEY = 'welcome_tour_auto_blocked';
const CONTEXTUAL_TOURS_BLOCK_KEY = 'contextual_tours_auto_blocked';

const POPOVER_CLASSES = [
  '!box-border',
  '!w-[48rem]',
  '!max-w-[calc(100vw-2rem)]',
  '!border-0',
  '!bg-transparent',
  '!p-0',
  '!shadow-none',
];

const ACTIVE_LAYER_CLASSES = [
  'product-tour-active-layer',
  '!z-[999999]',
  '!bg-n-background',
  '!shadow-2xl',
];

const HIDDEN_POPOVER_CLASSES = ['!hidden'];
const COMPACT_TARGET_MAX_HEIGHT = 48;

const TARGET_INDICATOR_CLASSES = [
  'product-tour-target-indicator',
  'absolute',
  'z-[1000001]',
  'flex',
  'size-7',
  'items-center',
  'justify-center',
  'rounded-full',
  'bg-n-brand',
  'text-[var(--n-brand-contrast)]',
  'shadow-2xl',
  'ring-2',
  'ring-n-brand/30',
  'pointer-events-none',
  'motion-safe:animate-tour-nudge',
  'motion-reduce:animate-none',
];

const TARGET_INDICATOR_DEFAULT_POSITION_CLASSES = ['right-3', 'top-3'];
const TARGET_INDICATOR_COMPACT_POSITION_CLASSES = ['right-1', 'top-1'];
const TARGET_INDICATOR_ICON_CLASSES = ['i-lucide-arrow-down-left', 'size-4'];

const delay = timeout =>
  new Promise(resolve => {
    setTimeout(resolve, timeout);
  });

const findStepElement = element => {
  const selectors = Array.isArray(element) ? element : [element];
  return selectors.find(selector => document.querySelector(selector));
};

const hasElement = selector => !!selector && !!document.querySelector(selector);

const hasAnyElement = selectors =>
  (Array.isArray(selectors) ? selectors : [selectors]).some(hasElement);

const isElementVisible = selector => {
  const element = document.querySelector(selector);
  return !!(
    element?.offsetWidth ||
    element?.offsetHeight ||
    element?.getClientRects().length
  );
};

const prepareVisibilityAction = ({ trigger, visibleTarget }, shouldClick) => {
  if (!shouldClick(visibleTarget)) return;

  if (typeof trigger === 'string') {
    document.querySelector(trigger)?.click();
    return;
  }

  if (trigger.eventName) {
    document.dispatchEvent(new CustomEvent(trigger.eventName));
    return;
  }

  document.querySelector(trigger.selector)?.click();
};

const hasPendingCondition = ({
  waitUntilGone,
  waitUntilPresent,
  waitUntilVisible,
}) => {
  return (
    (waitUntilGone && hasElement(waitUntilGone)) ||
    (waitUntilPresent && !hasElement(waitUntilPresent)) ||
    (waitUntilVisible && !isElementVisible(waitUntilVisible))
  );
};

const isStepApplicable = step =>
  (!step.includeWhenPresent || hasAnyElement(step.includeWhenPresent)) &&
  (!step.excludeWhenPresent || !hasAnyElement(step.excludeWhenPresent));

const getRequiredSteps = steps =>
  steps.filter(step => !step.optional && isStepApplicable(step));

const hasPendingStepConditions = steps =>
  steps.some(step => {
    if (step.optional) return false;
    if (!isStepApplicable(step)) return false;

    return hasPendingCondition(step);
  });

const prepareStepVisibility = step => {
  if (step.collapseBefore) {
    prepareVisibilityAction(
      step.collapseBefore,
      visibleTarget => !visibleTarget || isElementVisible(visibleTarget)
    );
  }

  if (step.expandBefore) {
    prepareVisibilityAction(
      step.expandBefore,
      visibleTarget => !visibleTarget || !isElementVisible(visibleTarget)
    );
  }
};

const shouldResolveElementOnDisplay = step =>
  step.resolveOnHighlight ||
  step.deferred ||
  step.collapseBefore ||
  step.expandBefore;

const getVisibleSteps = steps =>
  steps.reduce((visibleSteps, step) => {
    if (!isStepApplicable(step)) {
      return visibleSteps;
    }

    const element = findStepElement(step.element);
    if (!element && !shouldResolveElementOnDisplay(step)) return visibleSteps;

    return [
      ...visibleSteps,
      {
        ...step,
        element: shouldResolveElementOnDisplay(step) ? step.element : element,
      },
    ];
  }, []);

const waitForRequiredTourElements = async (steps, startedAt = Date.now()) => {
  if (
    hasPendingStepConditions(steps) &&
    Date.now() - startedAt < WAIT_TIMEOUT
  ) {
    await delay(WAIT_INTERVAL);
    return waitForRequiredTourElements(steps, startedAt);
  }

  const visibleSteps = getVisibleSteps(steps);
  const requiredSteps = getRequiredSteps(steps);
  const visibleRequiredSteps = visibleSteps.filter(step => !step.optional);
  if (
    visibleRequiredSteps.length === requiredSteps.length ||
    Date.now() - startedAt >= WAIT_TIMEOUT
  ) {
    return visibleSteps;
  }

  await delay(WAIT_INTERVAL);
  return waitForRequiredTourElements(steps, startedAt);
};

const waitForOptionalTourElements = async (steps, startedAt = Date.now()) => {
  const optionalSteps = steps.filter(step => step.optional);
  const visibleOptionalSteps = getVisibleSteps(optionalSteps);
  const hasPendingOptionalConditions = optionalSteps.some(step => {
    return (
      !findStepElement(step.element) &&
      step.waitUntilPresent &&
      !hasElement(step.skipWhenPresent) &&
      !hasElement(step.waitUntilPresent)
    );
  });

  if (
    !hasPendingOptionalConditions ||
    visibleOptionalSteps.length === optionalSteps.length ||
    Date.now() - startedAt >= OPTIONAL_WAIT_TIMEOUT
  ) {
    return getVisibleSteps(steps);
  }

  await delay(WAIT_INTERVAL);
  return waitForOptionalTourElements(steps, startedAt);
};

const applyClasses = (element, classes) => {
  element?.classList.add(...classes);
};

const clearTargetIndicators = () => {
  document
    .querySelectorAll('.product-tour-target-indicator')
    .forEach(marker => marker.remove());
  document.querySelectorAll('[data-product-tour-anchor]').forEach(element => {
    if (element.dataset.productTourAddedPosition) {
      element.classList.remove('!relative');
    }
    element.removeAttribute('data-product-tour-anchor');
    element.removeAttribute('data-product-tour-added-position');
  });
};

const addTargetIndicator = element => {
  clearTargetIndicators();
  if (!element) return;

  const styles = window.getComputedStyle(element);
  if (styles.position === 'static') {
    applyClasses(element, ['!relative']);
    element.dataset.productTourAddedPosition = 'true';
  }
  element.setAttribute('data-product-tour-anchor', 'true');

  const marker = document.createElement('span');
  const icon = document.createElement('span');
  marker.setAttribute('aria-hidden', 'true');
  applyClasses(marker, TARGET_INDICATOR_CLASSES);
  applyClasses(
    marker,
    element.getBoundingClientRect().height <= COMPACT_TARGET_MAX_HEIGHT
      ? TARGET_INDICATOR_COMPACT_POSITION_CLASSES
      : TARGET_INDICATOR_DEFAULT_POSITION_CLASSES
  );
  applyClasses(icon, TARGET_INDICATOR_ICON_CLASSES);
  marker.appendChild(icon);
  element.appendChild(marker);
};

const clearDriverActiveElements = () => {
  document.querySelectorAll('.driver-active-element').forEach(element => {
    element.classList.remove('driver-active-element');
    element.removeAttribute('aria-haspopup');
    element.removeAttribute('aria-expanded');
    element.removeAttribute('aria-controls');
  });
};

const clearActiveLayerElements = () => {
  document.querySelectorAll('.product-tour-active-layer').forEach(element => {
    element.classList.remove(...ACTIVE_LAYER_CLASSES);
  });
};

const hasVisibleTourBlocker = () =>
  TOUR_BLOCKING_SELECTORS.some(selector => isElementVisible(selector));

const isLicenseGateBlocking = (store, startedAt = Date.now()) => {
  const licenseState = store.state?.license;
  const hasVisibleBlocker = hasVisibleTourBlocker();
  if (!licenseState) return hasVisibleBlocker;

  if (hasVisibleBlocker) return true;

  const isWaitingForLicenseState =
    !licenseState.hasValidated ||
    !!store.getters?.['license/shouldShowPaywall'];

  return (
    isWaitingForLicenseState &&
    Date.now() - startedAt < LICENSE_VALIDATION_WAIT_TIMEOUT
  );
};

const waitForTourGate = async (store, startedAt = Date.now()) => {
  if (!isLicenseGateBlocking(store, startedAt)) return;

  await delay(WAIT_INTERVAL);
  await waitForTourGate(store, startedAt);
};

const prepareStep = step => {
  if (step.visibilityPrepared) return;
  prepareStepVisibility(step);
};

const normalizeClickTarget = target => {
  if (typeof target === 'string') {
    return { selector: target, timeout: 0, defer: false };
  }

  return {
    defer: false,
    timeout: 0,
    ...target,
  };
};

const waitForDeferredClick = async defer => {
  if (!defer) return;

  await delay(typeof defer === 'number' ? defer : 0);
};

const clickStepTarget = async (target, startedAt = Date.now()) => {
  const { defer, eventName, selector, timeout } = normalizeClickTarget(target);

  if (eventName) {
    await waitForDeferredClick(defer);
    document.dispatchEvent(new CustomEvent(eventName));
    return;
  }

  const element = document.querySelector(selector);

  if (element) {
    await waitForDeferredClick(defer);
    document.querySelector(selector)?.click();
    return;
  }

  if (Date.now() - startedAt >= timeout) return;

  await delay(WAIT_INTERVAL);
  await clickStepTarget(target, startedAt);
};

const clickStepTargets = targets => {
  const clickTargets = Array.isArray(targets) ? targets : [targets];
  return Promise.all(clickTargets.map(target => clickStepTarget(target)));
};

const waitForStepConditions = async (conditions, startedAt = Date.now()) => {
  const waitConditions = Array.isArray(conditions) ? conditions : [conditions];
  const pendingConditions = waitConditions.filter(condition =>
    hasPendingCondition(condition)
  );
  const hasPendingConditions = pendingConditions.length > 0;

  if (!hasPendingConditions) return true;
  if (Date.now() - startedAt >= WAIT_TIMEOUT) return false;

  await Promise.all(
    pendingConditions.map(condition =>
      condition.retryAction ? clickStepTargets(condition.retryAction) : null
    )
  );
  await delay(WAIT_INTERVAL);
  return waitForStepConditions(waitConditions, startedAt);
};

export function useProductTour(systemKey) {
  const { t } = useI18n();
  const store = useStore();
  const { uiSettings, updateUISettings } = useUISettings();
  const config = computed(() => getContextualHelpConfig(systemKey));

  const completionKey = computed(() => config.value?.tourCompletedKey);
  const isWelcomeTourAutoBlocked = computed(
    () =>
      !!uiSettings.value?.[WELCOME_TOUR_BLOCK_KEY] ||
      !!uiSettings.value?.disable_all_tours
  );
  const isContextualToursAutoBlocked = computed(
    () =>
      !!uiSettings.value?.[CONTEXTUAL_TOURS_BLOCK_KEY] ||
      !!uiSettings.value?.disable_all_tours
  );
  const isCompleted = computed(() => {
    if (!completionKey.value) return true;
    return !!uiSettings.value?.[completionKey.value];
  });

  const isAutoBlocked = (tourType = PRODUCT_TOUR_TYPES.CONTEXTUAL) => {
    if (tourType === PRODUCT_TOUR_TYPES.WELCOME) {
      return isWelcomeTourAutoBlocked.value;
    }

    return isContextualToursAutoBlocked.value;
  };

  const markCompleted = () => {
    if (!completionKey.value) return Promise.resolve();
    return updateUISettings({ [completionKey.value]: true });
  };

  const startTour = async ({ markAsCompleted = true } = {}) => {
    if (!config.value) return;

    await waitForTourGate(store);
    await waitForRequiredTourElements(config.value.steps);
    const visibleSteps = await waitForOptionalTourElements(config.value.steps);
    if (!visibleSteps.length) return;

    const [{ driver }] = await Promise.all([
      import('driver.js'),
      import('driver.js/dist/driver.css'),
    ]);

    const dismissTour = ({ driver: currentTour, settings }) => {
      updateUISettings(settings);
      currentTour?.destroy();
    };

    let mountedPopover;

    const decoratePopover = (popover, { driver: currentTour } = {}) => {
      applyClasses(popover.wrapper, POPOVER_CLASSES);
      applyClasses(popover.title, HIDDEN_POPOVER_CLASSES);
      applyClasses(popover.description, HIDDEN_POPOVER_CLASSES);
      applyClasses(popover.footer, HIDDEN_POPOVER_CLASSES);
      applyClasses(popover.closeButton, HIDDEN_POPOVER_CLASSES);
      popover.arrow?.remove();

      mountedPopover?.unmount();
      popover.wrapper?.querySelector('.product-tour-vue-host')?.remove();
      const host = document.createElement('div');
      host.className = 'product-tour-vue-host';
      popover.wrapper?.appendChild(host);

      const activeIndex = currentTour?.getActiveIndex?.() || 0;
      const activeStep = visibleSteps[activeIndex];
      mountedPopover = createApp(ProductTourModal, {
        tourTitle: config.value?.titleKey
          ? t(config.value.titleKey)
          : t('PRODUCT_TOUR.TITLE'),
        title: t(activeStep.titleKey),
        description: t(activeStep.descriptionKey),
        stepTitles: visibleSteps.map(step => t(step.titleKey)),
        currentIndex: activeIndex,
        labels: {
          eyebrow: t('PRODUCT_TOUR.EYEBROW'),
          options: t('PRODUCT_TOUR.OPTIONS'),
          close: t('GENERAL.CLOSE'),
          steps: t('PRODUCT_TOUR.STEPS'),
          progress: t('PRODUCT_TOUR.PROGRESS', {
            current: '{current}',
            total: '{total}',
          }),
          previous: t('PRODUCT_TOUR.PREV'),
          next: t('PRODUCT_TOUR.NEXT'),
          done: t('PRODUCT_TOUR.DONE'),
          dismissLabel: t('PRODUCT_TOUR.DISMISS_LABEL'),
          dismissCurrent: t('PRODUCT_TOUR.DISMISS_CURRENT'),
          dismissAll: t('PRODUCT_TOUR.DISMISS_ALL'),
          dismissCurrentDescription: t(
            'PRODUCT_TOUR.DISMISS_CURRENT_DESCRIPTION'
          ),
          dismissAllDescription: t('PRODUCT_TOUR.DISMISS_ALL_DESCRIPTION'),
          highlightHint: t('PRODUCT_TOUR.HIGHLIGHT_HINT'),
        },
        onClose: () => currentTour.destroy(),
        onNext: () => popover.nextButton?.click(),
        onPrevious: () => popover.previousButton?.click(),
        onGoToStep: index => currentTour.drive(index),
        onDismissCurrent: () => {
          if (!completionKey.value) return currentTour.destroy();
          return dismissTour({
            driver: currentTour,
            settings: { [completionKey.value]: true },
          });
        },
        onDismissAll: () =>
          dismissTour({
            driver: currentTour,
            settings: { disable_all_tours: true },
          }),
      });
      mountedPopover.mount(host);
    };

    const tour = driver({
      showProgress: true,
      allowClose: true,
      stagePadding: 8,
      stageRadius: 8,
      popoverOffset: 12,
      nextBtnText: t('PRODUCT_TOUR.NEXT'),
      prevBtnText: t('PRODUCT_TOUR.PREV'),
      doneBtnText: t('PRODUCT_TOUR.DONE'),
      progressText: t('PRODUCT_TOUR.PROGRESS', {
        current: '{{current}}',
        total: '{{total}}',
      }),
      onPopoverRender: decoratePopover,
      onDestroyed: () => {
        clearTargetIndicators();
        mountedPopover?.unmount();
        mountedPopover = null;
      },
      steps: visibleSteps.map((step, index) => {
        const isLastStep = index === visibleSteps.length - 1;
        const popover = {
          title: t(step.titleKey),
          description: t(step.descriptionKey),
        };

        if (step.clickOnNext || isLastStep) {
          popover.onNextClick = async (
            _element,
            _step,
            { driver: currentTour }
          ) => {
            if (step.clickOnNext) {
              await clickStepTargets(step.clickOnNext);
            }

            if (step.waitAfterNext) {
              const isReady = await waitForStepConditions(step.waitAfterNext);
              if (!isReady) return;
            }

            if (isLastStep && markAsCompleted) await markCompleted();
            currentTour.moveNext();
          };
        }

        const resolveElement = () => {
          clearDriverActiveElements();
          clearActiveLayerElements();
          prepareStep(step);
          step.visibilityPrepared = true;
          const element = document.querySelector(findStepElement(step.element));
          if (step.activeLayer) applyClasses(element, ACTIVE_LAYER_CLASSES);
          return element;
        };

        return {
          element: shouldResolveElementOnDisplay(step)
            ? resolveElement
            : step.element,
          onHighlightStarted: element => {
            prepareStep(step);
            addTargetIndicator(element);
          },
          onDeselected: () => {
            clearTargetIndicators();
            if (step.collapseOnDeselected) {
              prepareVisibilityAction(
                step.collapseOnDeselected,
                visibleTarget =>
                  !visibleTarget || isElementVisible(visibleTarget)
              );
            }

            if (step.activeLayer) clearActiveLayerElements();

            if (step.clickOnDeselected) {
              clickStepTargets(step.clickOnDeselected);
            }
          },
          popover,
        };
      }),
    });

    tour.drive();
  };

  const startIfPending = ({
    tourType = PRODUCT_TOUR_TYPES.CONTEXTUAL,
    beforeStart,
  } = {}) => {
    if (isAutoBlocked(tourType)) return Promise.resolve();
    if (isCompleted.value) return Promise.resolve();
    return Promise.resolve(beforeStart?.()).then(() => startTour());
  };

  return {
    isCompleted,
    isContextualToursAutoBlocked,
    isWelcomeTourAutoBlocked,
    markCompleted,
    startIfPending,
    startTour,
  };
}
